import React, { Component } from 'react';
import { Badge } from './ui';
import { SkillStatus, SessionStatus, AgentStatus, TaskStatus, KnowledgeBaseStatus, KnowledgeVisibility, DocumentStatus } from '../../types';
export function PageContainer({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className="mx-auto w-full max-w-6xl px-4 py-6 sm:px-6" data-id="element-200">{children}</div>;
}
export function PageHeader({
  title,
  description,
  action
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-201">
      <div data-id="element-202">
        <h2 className="text-xl font-semibold text-slate-100" data-id="element-203">{title}</h2>
        {description && <p className="mt-1 text-sm text-slate-400" data-id="element-204">{description}</p>}
      </div>
      {action && <div className="flex shrink-0 gap-2" data-id="element-205">{action}</div>}
    </div>;
}
export function EmptyState({
  icon: Icon,
  title,
  description,
  action
}: {
  icon: ComponentType<{
    className?: string;
  }>;
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-700 px-6 py-14 text-center" data-id="element-206">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-800" data-id="element-207">
        <Icon className="h-6 w-6 text-slate-500" data-id="element-208" />
      </div>
      <p className="mt-3 text-sm font-medium text-slate-300" data-id="element-209">{title}</p>
      {description && <p className="mt-1 max-w-sm text-sm text-slate-500" data-id="element-210">{description}</p>}
      {action && <div className="mt-4" data-id="element-211">{action}</div>}
    </div>;
}
const skillStatusMap: Record<SkillStatus, {
  tone: any;
  label: string;
}> = {
  installing: {
    tone: 'amber',
    label: '安装中'
  },
  enabled: {
    tone: 'green',
    label: '已启用'
  },
  disabled: {
    tone: 'slate',
    label: '已禁用'
  },
  failed: {
    tone: 'red',
    label: '失败'
  }
};
export function SkillStatusBadge({
  status
}: {
  status: SkillStatus;
}) {
  const m = skillStatusMap[status];
  return <Badge tone={m.tone} data-id="element-212">{m.label}</Badge>;
}
const agentStatusMap: Record<AgentStatus, {
  tone: any;
  label: string;
}> = {
  draft: {
    tone: 'slate',
    label: '草稿'
  },
  active: {
    tone: 'green',
    label: '已启用'
  },
  disabled: {
    tone: 'red',
    label: '已禁用'
  }
};
export function AgentStatusBadge({
  status
}: {
  status: AgentStatus;
}) {
  const m = agentStatusMap[status];
  return <Badge tone={m.tone} data-id="element-213">{m.label}</Badge>;
}
const taskStatusMap: Record<TaskStatus, {
  tone: any;
  label: string;
}> = {
  pending: {
    tone: 'slate',
    label: '等待中'
  },
  running: {
    tone: 'blue',
    label: '运行中'
  },
  completed: {
    tone: 'green',
    label: '已完成'
  },
  failed: {
    tone: 'red',
    label: '失败'
  },
  cancelled: {
    tone: 'slate',
    label: '已取消'
  }
};
export function TaskStatusBadge({
  status
}: {
  status: TaskStatus;
}) {
  const m = taskStatusMap[status];
  return <Badge tone={m.tone} data-id="element-214">{m.label}</Badge>;
}
const sourceMap: Record<string, {
  tone: any;
  label: string;
}> = {
  upload: {
    tone: 'blue',
    label: '上传'
  },
  ai_generated: {
    tone: 'purple',
    label: 'AI 创建'
  },
  third_party: {
    tone: 'amber',
    label: '第三方'
  }
};
export function SkillSourceBadge({
  source
}: {
  source: string;
}) {
  const m = sourceMap[source] || {
    tone: 'slate',
    label: source
  };
  return <Badge tone={m.tone} data-id="element-215">{m.label}</Badge>;
}
export function KnowledgeBaseStatusBadge({
  status
}: {
  status: KnowledgeBaseStatus;
}) {
  const map: Record<KnowledgeBaseStatus, {
    tone: any;
    label: string;
  }> = {
    indexing: {
      tone: 'amber',
      label: '索引中'
    },
    ready: {
      tone: 'green',
      label: '就绪'
    },
    failed: {
      tone: 'red',
      label: '失败'
    },
    disabled: {
      tone: 'slate',
      label: '已禁用'
    }
  };
  const m = map[status];
  return <Badge tone={m.tone} data-id="element-216">{m.label}</Badge>;
}
export function KnowledgeVisibilityBadge({
  visibility
}: {
  visibility: KnowledgeVisibility;
}) {
  const map: Record<KnowledgeVisibility, {
    tone: any;
    label: string;
  }> = {
    private: {
      tone: 'slate',
      label: '私有'
    },
    team: {
      tone: 'blue',
      label: '团队'
    },
    public: {
      tone: 'purple',
      label: '公开'
    }
  };
  const m = map[visibility];
  return <Badge tone={m.tone} data-id="element-217">{m.label}</Badge>;
}
export function DocumentStatusBadge({
  status
}: {
  status: DocumentStatus;
}) {
  const map: Record<DocumentStatus, {
    tone: any;
    label: string;
  }> = {
    uploaded: {
      tone: 'slate',
      label: '已上传'
    },
    parsing: {
      tone: 'amber',
      label: '解析中'
    },
    chunking: {
      tone: 'amber',
      label: '切片中'
    },
    embedding: {
      tone: 'amber',
      label: '向量化'
    },
    indexed: {
      tone: 'green',
      label: '已索引'
    },
    failed: {
      tone: 'red',
      label: '失败'
    }
  };
  const m = map[status];
  return <Badge tone={m.tone} data-id="element-218">{m.label}</Badge>;
}